import { RootState } from '@/store';
import { isReportInClinic, LONG_HYPHEN } from '@/utils/constants';
import { formatUtcDate } from '@/utils/formatDate';

import { MonitoredValuesForm } from '@/pages/physician/TransmissionReportPage/utils/types';
import { Alert } from '@/types/alert';
import { DeviceVendor } from '@/types/matchDevice';
import { Organization } from '@/types/organization';
import { ReportType, TransmissionReport } from '@/types/transmissionReport';
import { PRACTITIONER_SIGNATURE_ROLES } from '@/types/user';
import { DeviceLeadLocation, VtDeviceLead, VtLeadVendor } from '@/types/vendorTransmissionReport';

import { transmissionReportApiSlice } from '../slices/api/transmissionReport/transmissionReportsApiSlice';

export const statusMap: Record<number, string> = {
  0: 'Inactive',
  1: 'Active',
  2: 'New Implant',
  3: 'Pending',
  4: 'Entered In Error'
};
export const getCurrentReport = (state: RootState, reportId?: number) => {
  if (!reportId) return null;
  const { data } =
    transmissionReportApiSlice.endpoints.getTransmissionReportDetails.select(reportId)(state);
  return data;
};

export type DeviceInfoDetails = {
  modelName: string;
  modelNumber: string;
  serialNumber: string;
  type: string;
  implantDate: string;
  vendor?: DeviceVendor;
  status?: string;
  patientId: string;
  id: string;
  isEriTracked: boolean;
  deviceTelemetry: string;
  isMriCompatibility?: boolean | null;
};

export const fetchDeviceInfoFromReport = (report: TransmissionReport): DeviceInfoDetails => {
  return {
    modelName: report.matchDevice?.modelName?.trim() || ' - ',
    modelNumber: report.matchDevice?.modelNumber?.trim() || ' - ',
    serialNumber: report.matchDevice?.serialNumber?.trim() || ' - ',
    type: report.matchDevice?.type || ' - ',
    implantDate:
      formatUtcDate(
        report.matchDevice?.implantDate ?? report?.device?.deviceImplantDate,
        'MM-DD-YYYY'
      ) || '-',
    vendor: report?.matchDevice?.vendor as DeviceVendor,
    status:
      statusMap[report?.matchDevice?.status as 0 | 1 | 2 | 3 | 4] ?? report?.matchDevice?.status,
    patientId: report?.patient?.id,
    id: report?.matchDevice?.id ?? '',
    deviceTelemetry: report.matchDevice?.deviceTelemetry || ' - ',
    isMriCompatibility: report.matchDevice?.isMriCompatibility,
    isEriTracked: report?.matchDevice?.isEriTracked ?? false
  };
};

export const selectDeviceInfo = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const values = fetchDeviceInfoFromReport(reportData.transmissionReport);
  return values;
};

export const getIsReportSecondSign = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return false;

  const { transmissionType, account } = reportData.transmissionReport || {};
  const { inClinicReportWorkflow, inClinicSingleSignRecipient, sentToEhr } = account || {};

  if (transmissionType === ReportType.InClinic || transmissionType === ReportType.RemoteInClinic) {
    if (inClinicReportWorkflow === 'firstSign' && inClinicSingleSignRecipient === 'practitioner') {
      return true;
    }
    return inClinicReportWorkflow === 'secondSign';
  }

  return sentToEhr === 'secondSign' || sentToEhr === 'pendingReview';
};

export const fetchDeviceLeadsFromReport = (
  report: TransmissionReport | undefined,
  leadVendorsList: VtLeadVendor[] | undefined
) => {
  if (!report || !leadVendorsList) return [];

  const leads = report.deviceLeads || [];
  return leads.map((lead) => {
    const manufacturer = leadVendorsList?.find(
      (vendor) => vendor.leadVendorId.toString() === lead.vendorId
    )?.leadVendorName;

    const hasAnyValidValue =
      (lead.leadModelNumber !== null && lead.leadModelNumber !== undefined) ||
      (lead.vendorId !== null && lead.vendorId !== undefined);

    return {
      leadId: lead.leadId,
      location: lead.leadLocation as DeviceLeadLocation,
      vendorId: lead.vendorId || '-',
      manufacturer: manufacturer || '-',
      model: lead.leadModelNumber || '-',
      implantDate: hasAnyValidValue
        ? formatUtcDate(lead.leadImplantDate, 'MM-DD-YYYY') || '-'
        : '-',
      serialNumber: lead.leadSerialNumber || '-',
      id: lead?.id || '-'
    };
  });
};

export const selectDeviceLeads = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  const { data: leadVendorsList } =
    transmissionReportApiSlice.endpoints.getLeadVendorsList.select()(state);
  if (!reportData) return null;

  const leads = fetchDeviceLeadsFromReport(reportData.transmissionReport, leadVendorsList || []);
  return leads;
};

export const fetchMonitoredValuesFromReport = (report: TransmissionReport): MonitoredValuesForm => {
  const { deviceLeads, deviceMeasurement } = report;
  const deviceLeadsMap = (deviceLeads ?? []).reduce(
    (acc, lead) => {
      acc[lead.leadLocation ?? ''] = lead;
      return acc;
    },
    {} as Record<string, VtDeviceLead>
  );
  const raLead = deviceLeadsMap[DeviceLeadLocation.RA];
  const rvLead = deviceLeadsMap[DeviceLeadLocation.RV];
  const lvLead = deviceLeadsMap[DeviceLeadLocation.LV];

  return {
    leadIds: {
      [DeviceLeadLocation.RA]: raLead?.leadId.toString(),
      [DeviceLeadLocation.RV]: rvLead?.leadId.toString(),
      [DeviceLeadLocation.LV]: lvLead?.leadId.toString()
    },
    sensing: {
      [DeviceLeadLocation.RA]: raLead?.sensing,
      [DeviceLeadLocation.RV]: rvLead?.sensing,
      [DeviceLeadLocation.LV]: lvLead?.sensing
    },
    impedance: {
      [DeviceLeadLocation.RA]: raLead?.impedance,
      [DeviceLeadLocation.RV]: rvLead?.impedance,
      [DeviceLeadLocation.LV]: lvLead?.impedance
    },
    threshold: {
      [DeviceLeadLocation.RA]: raLead?.threshold,
      [DeviceLeadLocation.RV]: rvLead?.threshold,
      [DeviceLeadLocation.LV]: lvLead?.threshold
    },
    pulsewidth: {
      [DeviceLeadLocation.RA]: raLead?.pulsewidth,
      [DeviceLeadLocation.RV]: rvLead?.pulsewidth,
      [DeviceLeadLocation.LV]: lvLead?.pulsewidth
    },
    highVoltageImpedance: {
      [DeviceLeadLocation.RA]: LONG_HYPHEN,
      [DeviceLeadLocation.RV]: deviceMeasurement?.hvImpedance,
      [DeviceLeadLocation.LV]: LONG_HYPHEN
    },
    svcImpedance: {
      [DeviceLeadLocation.RA]: raLead?.svcImpedance,
      [DeviceLeadLocation.RV]: rvLead?.svcImpedance,
      [DeviceLeadLocation.LV]: lvLead?.svcImpedance
    },
    percentPaced: {
      [DeviceLeadLocation.RA]: deviceMeasurement?.apAPacePercent,
      [DeviceLeadLocation.RV]: deviceMeasurement?.rvpRvPacePercent,
      [DeviceLeadLocation.LV]: deviceMeasurement?.lvpPercent
    },
    afBurden: {
      [DeviceLeadLocation.RA]: deviceMeasurement?.afBurden,
      [DeviceLeadLocation.RV]: LONG_HYPHEN,
      [DeviceLeadLocation.LV]: LONG_HYPHEN
    }
  };
};

export const selectMonitoredValues = (
  state: RootState,
  reportId?: number
): MonitoredValuesForm | null => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const values = fetchMonitoredValuesFromReport(report);
  return values;
};

export const fetchBatteryStatusFromReport = (report: TransmissionReport) => {
  const device = report.device;
  return {
    voltage: device?.batteryVoltage || '',
    longevity: device?.batteryRemainingLongevity || '',
    percentRemaining: device?.batteryPercentRemaining || '',
    chargeTime: device?.capacitorChargeTime || '',
    batteryMeasurementTime: device?.batteryMeasurementTime ?? '',
    batteryStatus: device?.batteryStatus || ''
  };
};

export const fetchProgrammedParametersFromReport = (report: TransmissionReport) => {
  const programmedParameter = report.programmedParameter;
  return {
    id: programmedParameter?.id,
    mode: programmedParameter?.mode,
    lowerRate: programmedParameter?.lowerRate,
    upperRate: programmedParameter?.upperRate,
    atOrAf: programmedParameter?.atOrAf,
    vtZone1: programmedParameter?.vtZone1,
    vtZone2: programmedParameter?.vtZone2,
    pvarp: programmedParameter?.pvarp,
    pvab: programmedParameter?.pvab,
    vf: programmedParameter?.vf
  };
};

export const selectEpisodes = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const episodes = report.alerts?.filter((alert) => alert.title !== 'Presenting') || [];

  return episodes.map((episode: Alert) => {
    return {
      id: episode.id,
      title: episode.title,
      notes: episode.notes || [],
      images: episode.images || [],
      createdAt: episode.createdAt,
      updatedAt: episode.updatedAt
    };
  });
};

export const selectPresenting = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const presentingEpisode = report.alerts?.find((alert) => alert.title === 'Presenting') || null;

  return presentingEpisode;
};

export const selectDeviceBatteryStatus = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  const values = fetchBatteryStatusFromReport(report);
  return values;
};

export const selectSignatureStatusAndColor = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return {
    signatureStatus: report.signatureStatus,
    color: report.color
  };
};

export const getUnderReviewByOtherUser = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return report?.underReviewByOtherUser ?? null;
};

export const getAreFieldsEditable = (state: RootState, checkPractitionerRole = false) => {
  if (checkPractitionerRole) {
    const userRole = state.auth.user?.role;
    const isPractitioner = userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
    if (isPractitioner) return false;
  }
  const { isActiveReportSigned, isEditingSignedReport } = state.transmissionReportForm;
  if (isActiveReportSigned && !isEditingSignedReport) return false;
  return true;
};

export const getIsPhysicianInterpretationEditable = (state: RootState) => {
  const userRole = state.auth.user?.role;
  const isPractitioner = userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
  const { isActiveReportSigned, isEditingSignedReport } = state.transmissionReportForm;
  if (isPractitioner && isActiveReportSigned && !isEditingSignedReport) return false;
  return true;
};

export const getIsPractitioner = (state: RootState) => {
  const userRole = state.auth.user?.role;
  return userRole ? PRACTITIONER_SIGNATURE_ROLES.includes(userRole) : false;
};

export const getIsReportInClinic = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;
  return isReportInClinic(reportData?.transmissionReport.transmissionType);
};

export const getIsRemoteReport = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.transmissionType === 'remote';
};

export const getIsILRDevice = (state: RootState, reportId?: number) => {
  if (!reportId) return false;

  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.device?.deviceType === 'ILR';
};

export const getSigningProvider = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  return reportData?.transmissionReport.signingProvider;
};

export const selectExcludedReports = (state: RootState, reportId?: number): string[] => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return [];

  const report = reportData.transmissionReport;
  return report.overrides?.reportsToExclude || [];
};

export const selectIsReportExcluded = (
  state: RootState,
  reportId?: number,
  reportKey?: string
): boolean => {
  if (!reportKey) return false;

  const excludedReports = selectExcludedReports(state, reportId);
  return excludedReports.includes(reportKey);
};

export const selectReportsOverrides = (state: RootState, reportId?: number) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) return null;

  const report = reportData.transmissionReport;
  return report.overrides || null;
};

export const selectInClinicSignRestrictions = (
  state: RootState,
  reportId?: number,
  clinicOrganization?: Organization
) => {
  const reportData = getCurrentReport(state, reportId);
  if (!reportData) {
    return {
      isInClinicOrRemoteInClinic: false,
      hasFirstSignWorkflow: false,
      inClinicSingleSignRecipient: null,
      isRestricted: false,
      signingProviderSet: false
    };
  }

  const report = reportData.transmissionReport;

  const parentOrganization = clinicOrganization?.parent;

  const inClinicReportWorkflow =
    parentOrganization?.inClinicReportWorkflow ?? clinicOrganization?.inClinicReportWorkflow;
  const inClinicSingleSignRecipient =
    parentOrganization?.inClinicSingleSignRecipient ??
    clinicOrganization?.inClinicSingleSignRecipient;

  const isInClinicOrRemoteInClinic = [ReportType.InClinic, ReportType.RemoteInClinic].includes(
    report?.transmissionType as ReportType
  );
  const hasFirstSignWorkflow = inClinicReportWorkflow === 'firstSign';

  const isRestricted = !!(
    isInClinicOrRemoteInClinic &&
    hasFirstSignWorkflow &&
    inClinicSingleSignRecipient
  );

  return {
    isInClinicOrRemoteInClinic,
    hasFirstSignWorkflow,
    inClinicSingleSignRecipient,
    isRestricted,
    signingProviderSet: !!report.signingProvider
  };
};
